import React, { useEffect, useState } from 'react'
import {
	Autocomplete,
	Button,
	Stack,
	TextField,
	Typography,
	Paper,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	Box,
	IconButton,
	CircularProgress,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { styled } from '@mui/material/styles'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
// import { searchTrips } from '../redux/actions/search'
// import { api } from '../config/api'
import { Search } from '@mui/icons-material'
import moment from 'moment'
import { ArrowRightLeft } from 'lucide-react'
import { api } from '../../config/api'
import _ from 'lodash'

const StyledPaper = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(3),
	borderRadius: theme.spacing(1),
	backgroundColor: 'white',
	boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
	marginBottom: theme.spacing(3),
	marginTop: theme.spacing(3),
}))

const SearchButton = styled(Button)(({ theme }) => ({
	height: '40px',
	borderRadius: '8px',
	padding: theme.spacing(0, 4),
}))

const SearchBooking = ({
	from,
	to,
	selectedDate,
	passengers,
	transportType,
	onSearch,
}) => {
	const navigate = useNavigate()
	const location = useLocation()

	const dispatch = useDispatch()

	// Change these selectors
	const [isFromLoading, setIsFromLoading] = useState(false)
	const [isToLoading, setIsToLoading] = useState(false)
	const [fromOptions, setFromOptions] = useState([])
	const [toOptions, setToOptions] = useState([])

	const [isLoading, setIsLoading] = useState(false)

	const fetchFromOptions = async selectedDate => {
		setIsFromLoading(true)
		try {
			// const url = `/bookingLocations/tripsLocations?departureDate=${new Date(selectedDate)}`
			const url = `/bookingLocations`

			const response = await api.get(url)
			const options = [...new Set(response.data.data)]
			let names = [...new Set(options.map(option => option.name))].sort();

			names = [
				...names.filter(name => name === 'Nairobi'),
				...names.filter(name => name !== 'Nairobi'),
			]

			setFromOptions(names)
		} catch (error) {
			console.error('Error fetching from options:', error.message)
		} finally {
			setIsFromLoading(false)
		}
	}

	const fetchToOptions = async selectedDate => {
		setIsToLoading(true)
		try {
			// const url = `/bookingLocations/tripsLocations?departureDate=${new Date(selectedDate)}`
			const url = `/bookingLocations`

			const response = await api.get(url)
			const options = [...new Set(response.data.data)]
			let names = [...new Set(options.map(option => option.name))].sort();

			names = [
				...names.filter(name => name === 'Nairobi'),
				...names.filter(name => name !== 'Nairobi'),
			]

			setToOptions(names);
		} catch (error) {
			console.error('Error fetching to options:', error.message)
		} finally {
			setIsToLoading(false)
		}
	}

	const departureDate = new Date()
	departureDate.setHours(23, 59, 59, 999)

	const [formState, setFormState] = useState({
		from: from || null,
		fromInput: '',
		to: to || null,
		toInput: '',
		selectedDate: moment().endOf('day'),
		passengers: passengers || 1,
		transportType: transportType || '',
	})

	const onSubmit = e => {
		e.preventDefault()
		setIsLoading(true)

		onSearch({
			departureDate: formState.selectedDate.valueOf(),
			from: formState.from,
			to: formState.to,
			passengers: formState.passengers,
			transportType: formState.transportType,
		})

		setIsLoading(false)

		if (location.pathname === '/') {
			navigate('/app/booking', { state: { formState } })
		}
	}

	useEffect(() => {
		fetchFromOptions(moment().endOf('day'))
		fetchToOptions(moment().endOf('day'))
	}, [])

	useEffect(() => {
		const debounceSearch = _.debounce(() => {
			// Only trigger search if we have all required fields
			if (formState.from && formState.to && formState.selectedDate) {
				onSearch({
					departureDate: formState.selectedDate.valueOf(),
					from: formState.from,
					to: formState.to,
					passengers: formState.passengers,
					transportType: formState.transportType,
				})
			}
		}, 1000) // Increased debounce time to reduce unnecessary calls

		// Only call debounceSearch if this isn't the initial render
		if (formState.from && formState.to && formState.selectedDate) {
			debounceSearch()
		}

		return () => debounceSearch.cancel()
	}, [formState])

	const handleFormChange = field => (event, value) => {
		// For Select components, we want event.target.value
		// For Autocomplete, we want the value parameter
		const newValue =
			field === 'from' || field === 'to' ? value : event.target.value

		setFormState(prev => ({
			...prev,
			[field]: newValue,
		}))
	}
	const handleSwap = () => {
		setFormState(prev => ({
			...prev,
			from: prev.to,
			to: prev.from,
			fromInput: prev.toInput,
			toInput: prev.fromInput,
		}))
	}

	const handleInputChange = field => (event, value) => {
		setFormState(prev => ({
			...prev,
			[`${field}Input`]: value,
		}))
	}

	const formElementStyles = {
		flex: '1 1 auto',
		minWidth: { xs: '100%', sm: '200px' },
		maxWidth: { xs: '100%', sm: '300px' },
	}

	const filteredToOptions = toOptions.filter(
		option => option !== formState.from,
	)

	const filteredFromOptions = fromOptions.filter(
		option => option !== formState.to,
	)

	return (
		<StyledPaper>
			<form
				onSubmit={onSubmit}
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: '16px',
					justifyContent: 'center',
					alignContent: 'center',
					alignItems: 'middle',
				}}
			>
				<div style={{ display: 'flex' }}>
					<Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
						Hassle-Free Travel starts here. Book Now!
					</Typography>
				</div>
				<Stack
					direction="row"
					flexWrap="wrap"
					gap={2}
					alignItems="center"
					justifyContent="flex-start"
					sx={{
						width: '100%',
						'& > *': {
							margin: '4px !important', // Override MUI spacing to ensure consistent gaps
						},
					}}
				>
					<Autocomplete
						value={formState.from}
						onChange={handleFormChange('from')}
						inputValue={formState.fromInput}
						onInputChange={handleInputChange('from')}
						options={filteredFromOptions}
						loading={isFromLoading}
						noOptionsText={
							isFromLoading ? 'Loading...' : 'No options available'
						}
						size="small"
						sx={formElementStyles}
						renderInput={params => (
							<TextField
								{...params}
								label="From"
								variant="outlined"
								error={
									formState.from === formState.to && formState.from !== null
								}
								helperText={
									formState.from === formState.to && formState.from !== null
										? 'Departure and destination cannot be the same'
										: ''
								}
							/>
						)}
					/>

					<IconButton
						type="button"
						onClick={handleSwap}
						aria-label="Swap locations"
						sx={{
							color: 'secondary.main',
							flex: '0 0 auto',
							alignSelf: 'center',
						}}
					>
						<ArrowRightLeft />
					</IconButton>

					<Autocomplete
						value={formState.to}
						onChange={handleFormChange('to')}
						inputValue={formState.toInput}
						onInputChange={handleInputChange('to')}
						options={filteredToOptions}
						loading={isToLoading}
						noOptionsText={isToLoading ? 'Loading...' : 'No options available'}
						size="small"
						sx={formElementStyles}
						renderInput={params => (
							<TextField
								{...params}
								label="To"
								variant="outlined"
								error={formState.from === formState.to && formState.to !== null}
								helperText={
									formState.from === formState.to && formState.to !== null
										? 'Departure and destination cannot be the same'
										: ''
								}
							/>
						)}
					/>

					<LocalizationProvider dateAdapter={AdapterMoment}>
						<DatePicker
							label="Departure Date"
							value={formState.selectedDate}
							onChange={newValue =>
								handleFormChange('selectedDate')({
									target: { value: newValue },
								})
							}
							minDate={moment()}
							sx={{
								...formElementStyles,
								'& .MuiInputBase-root': {
									height: 40,
									fontSize: '0.875rem',
								},
							}}
						/>
					</LocalizationProvider>

					<FormControl
						sx={{
							...formElementStyles,
							minWidth: { xs: '100%', sm: '120px' },
							maxWidth: { xs: '100%', sm: '150px' },
						}}
					>
						<InputLabel>Passengers</InputLabel>
						<Select
							value={formState.passengers}
							label="Passengers"
							onChange={handleFormChange('passengers')}
							size="small"
						>
							{[1, 2, 3, 4, 5, 6].map(num => (
								<MenuItem key={num} value={num}>
									{num}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl
						sx={{
							...formElementStyles,
							minWidth: { xs: '100%', sm: '120px' },
							maxWidth: { xs: '100%', sm: '150px' },
						}}
					>
						<InputLabel>Transport</InputLabel>
						<Select
							value={formState.transportType}
							label="Transport"
							onChange={handleFormChange('transportType')}
							size="small"
						>
							<MenuItem value="bus">Bus</MenuItem>
							{/* <MenuItem value="train">Train</MenuItem> */}
							<MenuItem value="shuttle">Shuttle</MenuItem>
						</Select>
					</FormControl>
				</Stack>
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						marginTop: '16px',
					}}
				>
					<SearchButton
						startIcon={
							isLoading || isToLoading || isFromLoading ? (
								<CircularProgress size={20} color="inherit" />
							) : (
								<Search />
							)
						}
						variant="contained"
						type="submit"
						disabled={
							!formState.from ||
							!formState.to ||
							!formState.selectedDate ||
							isLoading || isToLoading || isFromLoading
						}
						// startIcon={<Search />}
						color="secondary"
					>
						{isLoading ? 'loading...' : 'Search'}
					</SearchButton>
				</div>
			</form>
		</StyledPaper>
	)
}

export default SearchBooking
